import React, { useState } from 'react';
import { X, Save, Bell, AlertTriangle, TrendingDown, Activity } from 'lucide-react';

const AlertConfiguration = ({ onClose, onSave }) => {
  const [alerts, setAlerts] = useState([
    {
      id: 1,
      name: 'Data Flow Interruption',
      type: 'flow',
      enabled: true,
      threshold: 0,
      duration: 5,
      severity: 'critical',
      channels: ['email', 'dashboard']
    },
    {
      id: 2,
      name: 'Quality Degradation',
      type: 'quality',
      enabled: true,
      threshold: 85,
      duration: 15,
      severity: 'high',
      channels: ['email', 'dashboard']
    },
    {
      id: 3,
      name: 'Rate Limit Warning',
      type: 'ratelimit',
      enabled: true,
      threshold: 80,
      duration: 0,
      severity: 'medium',
      channels: ['dashboard']
    },
    {
      id: 4,
      name: 'Platform Connection Failure',
      type: 'connection',
      enabled: true,
      threshold: 0,
      duration: 1,
      severity: 'critical',
      channels: ['email', 'sms', 'dashboard']
    }
  ]);

  const [editingAlert, setEditingAlert] = useState(null);

  const alertTypes = [
    { value: 'flow', label: 'Data Flow', icon: Activity },
    { value: 'quality', label: 'Quality', icon: TrendingDown },
    { value: 'ratelimit', label: 'Rate Limit', icon: AlertTriangle },
    { value: 'connection', label: 'Connection', icon: Bell }
  ];

  const severityOptions = [
    { value: 'low', label: 'Low', color: 'bg-blue-100 text-blue-800' },
    { value: 'medium', label: 'Medium', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'high', label: 'High', color: 'bg-orange-100 text-orange-800' },
    { value: 'critical', label: 'Critical', color: 'bg-red-100 text-red-800' }
  ];

  const channelOptions = [
    { value: 'email', label: 'Email' },
    { value: 'sms', label: 'SMS' },
    { value: 'dashboard', label: 'Dashboard' },
    { value: 'webhook', label: 'Webhook' }
  ];

  const handleToggle = (alertId) => {
    setAlerts(prev => prev?.map(alert =>
      alert?.id === alertId ? { ...alert, enabled: !alert?.enabled } : alert
    ));
  };

  const handleEdit = (alert) => {
    setEditingAlert({ ...alert });
  };

  const handleSaveEdit = () => {
    if (editingAlert) {
      setAlerts(prev => prev?.map(alert =>
        alert?.id === editingAlert?.id ? editingAlert : alert
      ));
      setEditingAlert(null);
    }
  };

  const handleChannelToggle = (channel) => {
    if (editingAlert) {
      setEditingAlert(prev => ({
        ...prev,
        channels: prev?.channels?.includes(channel)
          ? prev?.channels?.filter(c => c !== channel)
          : [...prev?.channels, channel]
      }));
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Alert Configuration</h2>
            <p className="text-sm text-gray-600 mt-1">
              Configure automated alerts for data flow interruptions and quality issues
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          <div className="space-y-4">
            {alerts?.map((alert) => (
              <div 
                key={alert?.id}
                className={`border rounded-lg p-4 ${
                  alert?.enabled ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-200 opacity-60'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="mt-1">
                      {React.createElement(
                        alertTypes?.find(t => t?.value === alert?.type)?.icon || Bell,
                        { className: 'w-5 h-5 text-blue-600' }
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-gray-900">{alert?.name}</h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          severityOptions?.find(s => s?.value === alert?.severity)?.color
                        }`}>
                          {alert?.severity?.charAt(0)?.toUpperCase() + alert?.severity?.slice(1)}
                        </span>
                      </div>
                      
                      <div className="text-sm text-gray-600 space-y-1">
                        {alert?.type === 'quality' && (
                          <p>Alert when quality falls below {alert?.threshold}% for {alert?.duration} minutes</p>
                        )}
                        {alert?.type === 'flow' && (
                          <p>Alert when data flow stops for {alert?.duration} minutes</p>
                        )}
                        {alert?.type === 'ratelimit' && (
                          <p>Alert when rate limit usage exceeds {alert?.threshold}%</p>
                        )}
                        {alert?.type === 'connection' && (
                          <p>Alert when platform connection fails for {alert?.duration} minute(s)</p>
                        )}
                        
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-xs text-gray-500">Channels:</span>
                          {alert?.channels?.map((channel, idx) => (
                            <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                              {channel}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEdit(alert)}
                      className="px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded transition-colors"
                    >
                      Edit
                    </button>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={alert?.enabled}
                        onChange={() => handleToggle(alert?.id)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => onSave(alerts)}
            className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Save className="w-4 h-4" />
            Save Configuration
          </button>
        </div>
      </div>
      {/* Edit Modal */}
      {editingAlert && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Edit Alert</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Alert Name
                </label>
                <input
                  type="text"
                  value={editingAlert?.name}
                  onChange={(e) => setEditingAlert(prev => ({ ...prev, name: e?.target?.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Severity
                </label>
                <select
                  value={editingAlert?.severity}
                  onChange={(e) => setEditingAlert(prev => ({ ...prev, severity: e?.target?.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {severityOptions?.map(option => (
                    <option key={option?.value} value={option?.value}>
                      {option?.label}
                    </option>
                  ))}
                </select>
              </div>

              {editingAlert?.type !== 'flow' && editingAlert?.type !== 'connection' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Threshold
                  </label>
                  <input
                    type="number"
                    value={editingAlert?.threshold}
                    onChange={(e) => setEditingAlert(prev => ({ ...prev, threshold: parseInt(e?.target?.value) }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min="0"
                    max="100"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Duration (minutes)
                </label>
                <input
                  type="number"
                  value={editingAlert?.duration}
                  onChange={(e) => setEditingAlert(prev => ({ ...prev, duration: parseInt(e?.target?.value) }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notification Channels
                </label>
                <div className="space-y-2">
                  {channelOptions?.map(channel => (
                    <label key={channel?.value} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editingAlert?.channels?.includes(channel?.value)}
                        onChange={() => handleChannelToggle(channel?.value)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{channel?.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setEditingAlert(null)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AlertConfiguration;